<template>
  <div class="rd-screen">
    <report-tools :copy="rows.length > 0" @copy="copyTable"></report-tools>

    <report-state v-if="loading" kind="loading"></report-state>
    <report-state
      v-else-if="!rows.length"
      icon="mdi-bowl-mix-outline"
      :title="emptyTitle"
      :text="emptyText"
    ></report-state>

    <div v-else class="rd-card">
      <table class="rd-tbl">
        <thead>
          <tr>
            <th scope="col" class="rd-col-num">#</th>
            <th scope="col" :aria-sort="ariaSort('name')">
              <button type="button" @click="sortBy('name')">{{ $t("meal") }} {{ sortArrow("name") }}</button>
            </th>
            <th
              v-for="shift in visibleShifts"
              :key="shift.id"
              scope="col"
              class="is-right"
              :aria-sort="ariaSort(shift.key)"
            >
              <button type="button" @click="sortBy(shift.key)">
                <report-shift-tag :shift="shift.id"></report-shift-tag>
                {{ sortArrow(shift.key) }}
              </button>
            </th>
            <th scope="col" class="is-right" :aria-sort="ariaSort('total')">
              <button type="button" @click="sortBy('total')">{{ $t("total") }} {{ sortArrow("total") }}</button>
            </th>
            <th scope="col" class="rd-col-share">{{ $t("report_desktop.share") }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, index) in rows" :key="row.key">
            <td class="rd-num rd-muted">{{ index + 1 }}</td>
            <td class="is-name" :title="row.name">{{ row.name }}</td>
            <td
              v-for="shift in visibleShifts"
              :key="shift.id"
              class="is-right rd-num"
              :class="{ 'is-zero': !row[shift.key] }"
            >
              {{ row[shift.key] }}
            </td>
            <td class="is-right rd-num"><b>{{ row.total }}</b></td>
            <td class="rd-col-share">
              <span class="rd-share">
                <span class="rd-mini"><i :style="{ width: `${row.relative}%` }"></i></span>
                <span class="rd-muted rd-num">{{ row.share }}%</span>
              </span>
            </td>
          </tr>
        </tbody>
        <tfoot>
          <tr>
            <td></td>
            <td>{{ $t("total") }}</td>
            <td v-for="shift in visibleShifts" :key="shift.id" class="is-right rd-num">{{ totals[shift.key] }}</td>
            <td class="is-right rd-num">{{ totals.total }}</td>
            <td class="rd-col-share"></td>
          </tr>
        </tfoot>
      </table>
    </div>
  </div>
</template>

<script>
import CompanyReportTabMeals from "@/components/company/reports/companyTabs/companyReportTabMeals.vue";
import { SHIFTS } from "@/constants/shifts";
import hasBarShifts from "@/components/report/shared/hasBarShifts";
import hasShiftSubtitle from "@/components/report/shared/hasShiftSubtitle";
import ReportShiftTag from "@/components/report/shared/ReportShiftTag.vue";
import { copyText, rowsToTsv } from "@/components/report/desktop/copyTable";
import hasTableSort from "@/components/report/desktop/hasTableSort";
import ReportState from "@/components/report/desktop/ReportState.vue";
import ReportTools from "@/components/report/desktop/ReportTools.vue";
import { hasPeriodRefetch } from "../../hasPeriodRefetch";

/**
 * Obroci firme na računaru: obrok × smena sa udjelom u ukupnom. Firme, smene, period i pretragu čita iz
 * zajedničke trake. Nasleđuje učitavanje, štampu i Excel iz starog taba.
 */
export default {
  name: "CompanyDesktopMeals",
  extends: CompanyReportTabMeals,
  mixins: [hasBarShifts, hasShiftSubtitle, hasTableSort, hasPeriodRefetch("fetchMeals")],
  components: { ReportShiftTag, ReportState, ReportTools },
  computed: {
    /** Smene 1–3 uvek (ako su izabrane), četvrta samo kad ima obroka. */
    visibleShifts() {
      return SHIFTS.filter(
        (shift) =>
          this.selectedShifts.includes(shift.id) &&
          (shift.id < 4 || this.filteredMeals.some((meal) => (meal[`shift${shift.id}`]?.quantity || 0) > 0))
      );
    },
    rows() {
      const found = this.search
        ? this.customSearch(this.filteredMeals, [{ value: "name" }], this.search)
        : this.filteredMeals;
      const visible = found.filter((meal) => meal.total > 0);
      const grand = visible.reduce((sum, meal) => sum + meal.total, 0);
      const max = Math.max(1, ...visible.map((meal) => meal.total));
      return this.sortRows(
        visible.map((meal, index) => {
          const row = {
            key: `${meal.id || meal.name}-${index}`,
            name: meal.name,
            total: meal.total,
            share: grand ? Math.round((meal.total / grand) * 100) : 0,
            relative: Math.round((meal.total / max) * 100),
          };
          SHIFTS.forEach((shift) => {
            row[shift.key] = meal[`shift${shift.id}`]?.quantity || 0;
          });
          return row;
        })
      );
    },
    totals() {
      const totals = { total: this.rows.reduce((sum, row) => sum + row.total, 0) };
      SHIFTS.forEach((shift) => {
        totals[shift.key] = this.rows.reduce((sum, row) => sum + row[shift.key], 0);
      });
      return totals;
    },
    emptyTitle() {
      return this.search
        ? this.$t("report_mobile.no_results_for", { search: this.search })
        : this.$t("report_mobile.no_meals");
    },
    emptyText() {
      return this.search ? "" : this.$t("report_desktop.empty_hint");
    },
    kpis() {
      return [
        { key: "total", label: this.$t("report_desktop.total_meals"), value: this.totals.total, tone: "inverse" },
        { key: "meals", label: this.$t("meals"), value: this.rows.length },
      ];
    },
  },
  watch: {
    kpis: {
      handler(kpis) {
        this.$emit("kpis", this.rows.length ? kpis : []);
      },
      immediate: true,
    },
    loading: {
      handler(loading) {
        this.$emit("loading", loading);
      },
      immediate: true,
    },
  },
  methods: {
    async copyTable() {
      const headers = [this.$t("meal"), ...this.visibleShifts.map((shift) => this.$t(shift.label)), this.$t("total")];
      const rows = this.rows.map((row) => [
        row.name,
        ...this.visibleShifts.map((shift) => row[shift.key]),
        row.total,
      ]);
      const copied = await copyText(rowsToTsv(headers, rows));
      this.$emit("toast", this.$t(copied ? "report_desktop.copied" : "report_desktop.copy_failed"));
    },
  },
};
</script>
